import React from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Divider,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { FaDiscord, FaGithub, FaEnvelope } from "react-icons/fa";
import { useTheme } from "@mui/material/styles";

const Footer = () => {
  const theme = useTheme();

  const socialLinks = [
    {
      icon: FaDiscord,
      href: "https://discord.gg/mspCBJp4E7",
      color: "#5865F2",
    },
    { icon: FaGithub, href: "https://github.com/mhome-ai", color: "#333" },
    { icon: FaEnvelope, href: "mailto:yw@mhome.ai", color: "#EA4335" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.glass,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)", // Safari support
        borderTop: `1px solid ${theme.palette.border.light}`,
        color: theme.palette.text.primary,
        py: 4,
        mt: 4,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          {/* Company Info */}
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <img
                src="/logo512.png"
                alt="mhome.ai logo"
                style={{
                  width: 32,
                  height: 32,
                  marginRight: 12,
                  borderRadius: 8,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: "1.3rem",
                }}
              >
                mhome.ai
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                maxWidth: 400,
                fontSize: "0.95rem",
              }}
            >
              Building the future of smart home with AI-driven voice control and
              intelligent automation solutions.
            </Typography>
          </Box>

          {/* Social Media Links */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {socialLinks.map((social, index) => (
              <IconButton
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.glassLight,
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 2,
                  p: 1.5,
                  transition: `all ${theme.transitions.duration.short}ms ease`,
                  "&:hover": {
                    color: social.color,
                    backgroundColor: theme.palette.background.glassStrong,
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    borderColor:
                      social.color === "#5865F2"
                        ? "rgba(88, 101, 242, 0.3)"
                        : "rgba(51, 51, 51, 0.3)",
                  },
                }}
              >
                <social.icon size={20} />
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Legal Claims Section */}
        <Divider
          sx={{
            borderColor: theme.palette.border.light,
            my: 3,
            opacity: 0.6,
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.tertiary,
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            © 2024 mhome.ai. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Link
              component={RouterLink}
              to="/support"
              sx={{
                color: theme.palette.text.tertiary,
                textDecoration: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                transition: `color ${theme.transitions.duration.short}ms ease`,
                "&:hover": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Customer Support
            </Link>
            <Link
              component={RouterLink}
              to="/privacy-policy"
              sx={{
                color: theme.palette.text.tertiary,
                textDecoration: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                transition: `color ${theme.transitions.duration.short}ms ease`,
                "&:hover": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              component={RouterLink}
              to="/terms-of-use"
              sx={{
                color: theme.palette.text.tertiary,
                textDecoration: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                transition: `color ${theme.transitions.duration.short}ms ease`,
                "&:hover": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
